import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import InfoIcon from '@mui/icons-material/Info';
import SideDrawer from '../../../../../common/_custom/SideDrawer/SideDrawer';
import LoadingSpinner from '../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import FinancialLoaderComponent from '../../../../../common/FinancialLoader/FinancialLoaderComponent';
import CategorySelector from '../../../../../common/CategorySelector/CategorySelector';
import { useIsMobile } from '../../../../../common/_custom/Hooks/useIsMobile';
import { AUDITOR_COMMENTS_LABEL } from '../../../../../utils/constants/commonConstants';
import { useAuditorCommentsDrawer, formatYear } from './useAuditorCommentsDrawer';
import { useAuditedType, buildConsolidationCategoryOptions } from '../useAuditedType';
import { AUDITOR_COMMENTS_COLUMN_LABELS, AUDITOR_COMMENTS_DRAWER_TITLE } from './constants';
import { AuditorDetails, AuditorCommentsContent, DirectorCommentsContent } from './components/AuditorCommentsCells/AuditorCommentsCells';
import AuditorCommentsYearPanel from './components/AuditorCommentsYearPanel/AuditorCommentsYearPanel';
import styles from './AuditorCommentsDrawer.module.scss';

interface AuditorCommentsDrawerProps {
    orgId: string;
    onClose: () => void;
}

const AuditorCommentsDrawer: React.FC<AuditorCommentsDrawerProps> = ({ orgId, onClose }) => {
    // `undefined` on initial load (backend auto-resolves); set to the uppercase nature
    // string after the user explicitly picks from the dropdown.
    const [nature, setNature] = useState<string | undefined>(undefined);
    const isMobile = useIsMobile();

    const onConsolidationChange = useCallback((newNature: string) => {
        setNature(newNature);
    }, []);

    const {
        auditedType,
        latestConsolidatedFY,
        latestStandaloneFY,
        auditedTypeOptionsObject,
        handleConsolidationChange,
        setAuditedTypeFromYears,
    } = useAuditedType(onConsolidationChange);

    const { loading, rows, lastUpdated, isMcaCalled } = useAuditorCommentsDrawer(orgId, nature, setAuditedTypeFromYears);

    const consolidationSelector = (latestConsolidatedFY !== null || latestStandaloneFY !== null) && (
        <CategorySelector
            categoryOptions={buildConsolidationCategoryOptions(auditedTypeOptionsObject)}
            selectedCategory={auditedType}
            changeSelectedTab={(type) => handleConsolidationChange(type)}
        />
    );
    const mcaFetchingStatus = isMcaCalled && (
        <span className={styles.LoaderMessage}>
            <LoadingSpinner color="var(--primary-text-colour)" height={isMobile ? '1rem' : undefined} className={styles.LoaderSpinner} />
            Fetching latest data from MCA (takes upto 24 hours)
        </span>
    );
    const lastUpdatedMeta = lastUpdated && <span className={styles.MetaItem}>Last Updated on {lastUpdated}</span>;
    const sourceBadge = !loading && rows.length > 0 && (
        <div className={styles.SourceBadge}>
            <span className={styles.SourceBadgeLabel}>Source:</span>
            <span className={styles.SourceBadgeValue}>MCA</span>
        </div>
    );
    const hasMobileControls = !!(consolidationSelector || sourceBadge);

    // Portalled to body: mounted in place, the drawer inherits whatever stacking context its
    // caller sits in (e.g. a sticky header), and anything outside that context with a higher
    // z-index paints over it — its own z-index: 999 cannot escape.
    return createPortal(
        <SideDrawer onClose={onClose} width="80%" heading={AUDITOR_COMMENTS_DRAWER_TITLE}>
            <SideDrawer.MobileLayout>
                {isMobile ? (
                    <SideDrawer.MobileHeader
                        title={AUDITOR_COMMENTS_DRAWER_TITLE}
                        status={
                            <>
                                {mcaFetchingStatus}
                                {lastUpdatedMeta}
                            </>
                        }
                        controls={
                            hasMobileControls && (
                                <>
                                    {consolidationSelector}
                                    {sourceBadge}
                                </>
                            )
                        }
                    />
                ) : (
                    <SideDrawer.Header>
                        <div className={styles.HeaderContainer}>
                            <div className={styles.HeaderLeft}>
                                <span className={styles.HeaderTitle}>{AUDITOR_COMMENTS_DRAWER_TITLE}</span>
                                {consolidationSelector}
                            </div>
                            <div className={styles.HeaderRight}>
                                {mcaFetchingStatus}
                                {lastUpdatedMeta}
                                {sourceBadge}
                            </div>
                        </div>
                    </SideDrawer.Header>
                )}
                <div className={clsx(styles.DrawerContent, { [styles.DrawerContentBelowHeader]: isMobile && !hasMobileControls })}>
                    {loading && (
                        <FinancialLoaderComponent
                            tableName={AUDITOR_COMMENTS_DRAWER_TITLE}
                            message="Fetching auditor comments data"
                            status={loading}
                        />
                    )}
                    {!loading && rows.length > 0 && isMobile && (
                        <div className={styles.PanelList} key={auditedType}>
                            {rows.map((row, i) => (
                                <AuditorCommentsYearPanel key={row.year} row={row} defaultExpanded={i === 0} />
                            ))}
                        </div>
                    )}
                    {!loading && (!isMobile || rows.length === 0) && (
                        <table className={styles.Table}>
                            <thead>
                                <tr className={styles.TableHeader}>
                                    <th>{AUDITOR_COMMENTS_COLUMN_LABELS.YEAR}</th>
                                    <th>{AUDITOR_COMMENTS_COLUMN_LABELS.AUDITOR_DETAILS}</th>
                                    <th>{AUDITOR_COMMENTS_LABEL}</th>
                                    <th>{AUDITOR_COMMENTS_COLUMN_LABELS.DIRECTOR_COMMENTS}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length > 0 ? (
                                    rows.map((row) => (
                                        <tr key={row.year} className={styles.TableRow}>
                                            <td className={styles.YearCell}>{formatYear(row.year)}</td>
                                            <td className={styles.AuditorDetailsCell}>
                                                <AuditorDetails row={row} />
                                            </td>
                                            <td className={styles.CommentsCell}>
                                                <AuditorCommentsContent row={row} />
                                            </td>
                                            <td className={styles.CommentsCell}>
                                                <DirectorCommentsContent row={row} />
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr className={`${styles.TableRow} ${styles.UnavailableDataTableRow}`}>
                                        <td colSpan={4}>
                                            <div className={styles.UnavailableDataRow}>
                                                <InfoIcon className={styles.UnavailableDataIcon} aria-hidden />
                                                <span className={styles.UnavailableDataMessage}>Not able to fetch data</span>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </SideDrawer.MobileLayout>
        </SideDrawer>,
        document.body,
    );
};

export default AuditorCommentsDrawer;
